import React from 'react';
import HelpBadge from './components/HelpBadge';

type ChecklistItem = { id: string; label: string; cost?: string };

type PipelineSettings = {
  company_id?: number;
  linkedin_insight_partner_id?: string;
  linkedin_insight_enabled?: boolean;
  linkedin_premium_business?: boolean;
  linkedin_sales_navigator?: boolean;
  linkedin_services_marketplace?: boolean;
  known_contact_enrich_provider?: string;
  known_contact_enrich_api_key_set?: boolean;
  rb2b_person_unmask?: boolean;
  notes?: string;
  checklist?: ChecklistItem[];
  saved?: boolean;
};

type Props = {
  gatewayBaseUrl: string;
  accessToken: string;
  companyId: number | null;
  language?: 'en' | 'es';
};

const RazzmatazzPipelinePanel: React.FC<Props> = ({
  gatewayBaseUrl,
  accessToken,
  companyId,
  language = 'en',
}) => {
  const spanish = language === 'es';
  const [data, setData] = React.useState<PipelineSettings | null>(null);
  const [partnerId, setPartnerId] = React.useState('');
  const [insightOn, setInsightOn] = React.useState(false);
  const [premiumOn, setPremiumOn] = React.useState(false);
  const [salesNavOn, setSalesNavOn] = React.useState(false);
  const [servicesOn, setServicesOn] = React.useState(true);
  const [provider, setProvider] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [note, setNote] = React.useState('');

  const headers = React.useMemo(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    }),
    [accessToken],
  );

  const load = React.useCallback(async () => {
    if (!companyId) {
      setData(null);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await fetch(
        `${gatewayBaseUrl}/api/v1/razzmatazz/pipeline?company_id=${companyId}`,
        { headers },
      );
      const payload = (await response.json().catch(() => ({}))) as PipelineSettings & { detail?: string };
      if (!response.ok) {
        throw new Error(typeof payload.detail === 'string' ? payload.detail : `HTTP ${response.status}`);
      }
      setData(payload);
      setPartnerId(String(payload.linkedin_insight_partner_id || ''));
      setInsightOn(Boolean(payload.linkedin_insight_enabled));
      setPremiumOn(Boolean(payload.linkedin_premium_business));
      setSalesNavOn(Boolean(payload.linkedin_sales_navigator));
      setServicesOn(payload.linkedin_services_marketplace !== false);
      setProvider(String(payload.known_contact_enrich_provider || ''));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [accessToken, companyId, gatewayBaseUrl, headers]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!companyId) return;
    setBusy(true);
    setError('');
    setNote('');
    try {
      const response = await fetch(`${gatewayBaseUrl}/api/v1/razzmatazz/pipeline`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          company_id: companyId,
          linkedin_insight_partner_id: partnerId.trim(),
          linkedin_insight_enabled: insightOn,
          linkedin_premium_business: premiumOn,
          linkedin_sales_navigator: salesNavOn,
          linkedin_services_marketplace: servicesOn,
          known_contact_enrich_provider: provider.trim(),
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as PipelineSettings & { detail?: string };
      if (!response.ok) {
        throw new Error(typeof payload.detail === 'string' ? payload.detail : 'Save failed');
      }
      setData(payload);
      setNote(spanish ? 'Pipeline guardado.' : 'Pipeline saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (!companyId) {
    return (
      <p className="razz-empty" role="status">
        {spanish ? 'Elige un proyecto para el pipeline GTM.' : 'Pick a project for the GTM pipeline.'}
      </p>
    );
  }

  return (
    <div className="razz-pipeline">
      <header className="razz-card">
        <p className="razz-kicker">Razzmatazz</p>
        <h3>
          {spanish ? 'Pipeline GTM' : 'GTM pipeline'}{' '}
          <HelpBadge label={spanish ? 'Qué es el pipeline' : 'What is the pipeline'}>
            <p>
              {spanish
                ? 'Insight Tag, Premium Business, Sales Navigator y enriquecimiento solo con asentimiento. No hay desanonimización tipo RB2B.'
                : 'Insight Tag, Premium Business, Sales Navigator, and consented enrichment only. No RB2B-style person unmasking.'}
            </p>
          </HelpBadge>
        </h3>
        <p className="razz-muted">{data?.notes || ''}</p>
      </header>

      {error ? <p className="razz-error" role="alert">{error}</p> : null}
      {note ? <p className="razz-muted" role="status">{note}</p> : null}

      <form
        className="razz-card"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <label className="razz-intake-source-row">
          <input type="checkbox" checked={insightOn} onChange={(e) => setInsightOn(e.target.checked)} disabled={busy} />
          <span>
            <strong>{spanish ? 'LinkedIn Insight Tag' : 'LinkedIn Insight Tag'}</strong>
            <span className="razz-muted"> {spanish ? 'Gratis · retargeting anónimo' : 'Free · anonymous retargeting'}</span>
          </span>
        </label>
        <label className="demo-field">
          <span>Partner ID</span>
          <input
            value={partnerId}
            onChange={(e) => setPartnerId(e.target.value)}
            placeholder="123456"
            disabled={busy}
          />
        </label>
        <p className="razz-muted">
          {spanish
            ? 'También puedes poner REACT_APP_LINKEDIN_INSIGHT_PARTNER_ID en el sitio.'
            : 'You can also set REACT_APP_LINKEDIN_INSIGHT_PARTNER_ID on the website build.'}
        </p>

        <label className="razz-intake-source-row">
          <input type="checkbox" checked={premiumOn} onChange={(e) => setPremiumOn(e.target.checked)} disabled={busy} />
          <span>
            <strong>Premium Business</strong>
            <span className="razz-muted"> ~$69.99/mo</span>
          </span>
        </label>
        <label className="razz-intake-source-row">
          <input type="checkbox" checked={salesNavOn} onChange={(e) => setSalesNavOn(e.target.checked)} disabled={busy} />
          <span>
            <strong>Sales Navigator</strong>
            <span className="razz-muted"> ~$120+/mo</span>
          </span>
        </label>
        <label className="razz-intake-source-row">
          <input type="checkbox" checked={servicesOn} onChange={(e) => setServicesOn(e.target.checked)} disabled={busy} />
          <span>
            <strong>{spanish ? 'Services Marketplace' : 'Services Marketplace'}</strong>
            <span className="razz-muted">
              {' '}
              {spanish ? 'leads → Social mail' : 'leads → Social mail'}
            </span>
          </span>
        </label>

        <label className="demo-field">
          <span>{spanish ? 'Enriquecimiento (solo asentimiento)' : 'Enrichment provider (assent only)'}</span>
          <select value={provider} onChange={(e) => setProvider(e.target.value)} disabled={busy}>
            <option value="">{spanish ? 'Ninguno' : 'None'}</option>
            <option value="moltsets">MoltSets</option>
            <option value="apollo">Apollo</option>
          </select>
        </label>
        <p className="razz-muted">
          {data?.known_contact_enrich_api_key_set
            ? (spanish ? 'Clave API detectada en el gateway.' : 'API key detected on the gateway.')
            : (spanish
              ? 'Sin MOLTSETS_API_KEY / APOLLO_API_KEY aún.'
              : 'No MOLTSETS_API_KEY / APOLLO_API_KEY yet.')}
          {' · '}
          {spanish
            ? 'RB2B person-unmask permanece desactivado.'
            : 'RB2B person-unmask stays off.'}
        </p>

        <button type="submit" className="dashboard-btn" disabled={busy}>
          {spanish ? 'Guardar' : 'Save'}
        </button>
      </form>

      <section className="razz-card">
        <h3>{spanish ? 'Lista de compras' : 'Buy / configure checklist'}</h3>
        <ul>
          {(data?.checklist || []).map((item) => (
            <li key={item.id}>
              <strong>{item.label}</strong>
              {item.cost ? <span className="razz-muted"> — {item.cost}</span> : null}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default RazzmatazzPipelinePanel;
